import React, { useState } from 'react';
import { Avatar } from 'src/design/atoms/Avatar';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { UnstyledButton } from 'src/design/atoms/UnstyledButton';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { Popover } from 'src/design/molecules/Popover';
import { Tabs } from 'src/design/molecules/Tabs';
import { useAuth } from 'src/hooks/useAuth';
import { ProfileTab } from 'src/app/navigation/ProfileTab';
import { useAccount, useMsal } from '@azure/msal-react';
import { IconLogout, IconUserFilled } from '@tabler/icons-react';
import { signOut } from 'src/auth/signOut';
import avatar1 from 'src/assets/profile.png';

/** @registry Header profile button: popover with user summary, logout action, and a Profile tab panel. */
export const Profile: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  const handleLogout = async () => {
    if (account?.homeAccountId) {
      signOut(instance, instance.getAccountByHomeId(account.homeAccountId));
    }
  };

  const [opened, setOpened] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('profile');

  if (!user) return null;
  return (
    <Box style={{ flexShrink: 0, marginLeft: 6 }}>
      <Popover
        opened={opened}
        onChange={setOpened}
        position="bottom-end"
        offset={9}
        shadow="md"
        width={290}
      >
        <Popover.Target>
          <UnstyledButton
            style={{ borderRadius: 4 }}
            bg={opened ? 'gray.3' : 'transparent'}
            p={4}
            onClick={() => setOpened((o) => !o)}
            aria-label="open profile"
          >
            <Group gap="sm" px={4}>
              <Avatar alt="profile user" src={avatar1} size={32} />
              <Text size="sm" fw={500}>
                {user.fullName}
              </Text>
            </Group>
          </UnstyledButton>
        </Popover.Target>

        <Popover.Dropdown p={0}>
          <Box px="md" pt="md" pb="sm">
            <Group justify="space-between" align="center">
              <Group gap="sm">
                <Avatar alt="profile user" src={avatar1} size={32} />
                <Stack gap={0}>
                  <Text fw={600}>{user.fullName}</Text>
                  <Text size="sm" c="dimmed">
                    {isAdmin ? 'Administrator' : 'Employee'}
                  </Text>
                </Stack>
              </Group>
              <BasicActionIcon
                variant="subtle"
                color="dark"
                size="lg"
                onClick={handleLogout}
              >
                <IconLogout />
              </BasicActionIcon>
            </Group>
          </Box>

          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List grow>
              <Tabs.Tab
                value="profile"
                leftSection={<IconUserFilled size={16} />}
              >
                Profile
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="profile">
              <ProfileTab handleLogout={handleLogout} />
            </Tabs.Panel>
          </Tabs>
        </Popover.Dropdown>
      </Popover>
    </Box>
  );
};
